import { useEffect, useMemo, useState } from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    apiService,
    type AnalyticsFilterParams,
    type SessionsMetricsResponse,
    type SessionsMetricsRow,
} from "../../services/api.service";
import { SortIcon } from "../feature-flags/SortIcon";
import { Button } from "../ui/button";
import { AnalyticsFilters, type FilterDisplayState } from "./AnalyticsFilters";
import { ChartCard, MetricCard, PlaceholderCard, SectionHeader } from "./AnalyticsUI";
import { formatCount, formatMinutesFromMs, formatPercent, formatSummaryStat, mean, percentile } from "./analyticsFormatters";
import { ANALYTICS_COLORS, ANALYTICS_SERIES } from "./analyticsTheme";

interface Props {
    filters: AnalyticsFilterParams;
    onFiltersChange: (f: AnalyticsFilterParams) => void;
    filterDisplay: FilterDisplayState;
    onFilterDisplayChange: (d: FilterDisplayState) => void;
}

export const AnalyticsSessions = ({ filters, onFiltersChange, filterDisplay, onFilterDisplayChange }: Props) => {
    const [lessonData, setLessonData] = useState<SessionsMetricsResponse | null>(null);
    const [dayData, setDayData] = useState<SessionsMetricsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadData = async (params: AnalyticsFilterParams = {}) => {
        try {
            setIsLoading(true);
            setError(null);
            const [lessonResult, dayResult] = await Promise.all([
                apiService.getSessionsMetrics("lesson", params),
                apiService.getSessionsMetrics("day", params),
            ]);
            setLessonData(lessonResult);
            setDayData(dayResult);
        } catch {
            setError("Failed to load session metrics");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData(filters);
    }, []);

    const handleFilterApply = (newFilters: AnalyticsFilterParams) => {
        onFiltersChange(newFilters);
        loadData(newFilters);
    };

    const sortedLessons = useMemo(() => {
        return [...(lessonData?.rows ?? [])].sort((a, b) => b.avg_time_ms - a.avg_time_ms).slice(0, 12);
    }, [lessonData]);

    type LessonSortKey = keyof SessionsMetricsRow;
    const [lessonSort, setLessonSort] = useState<{ key: LessonSortKey; dir: "asc" | "desc" }>({
        key: "avg_time_ms",
        dir: "desc",
    });

    const handleLessonSort = (key: LessonSortKey) => {
        setLessonSort((prev) => ({
            key,
            dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc",
        }));
    };

    const sortedLessonsTable = useMemo(() => {
        const rows = [...(lessonData?.rows ?? [])];
        rows.sort((a, b) => {
            const av = (a[lessonSort.key] as number) ?? 0;
            const bv = (b[lessonSort.key] as number) ?? 0;
            return lessonSort.dir === "asc" ? av - bv : bv - av;
        });
        return rows;
    }, [lessonData, lessonSort]);

    const sessionTrend = dayData?.rows.map((row) => ({ label: row.label, value: row.sessions })) ?? [];
    const completionTrend = dayData?.rows.map((row) => ({ label: row.label, value: row.avg_completion_pct })) ?? [];
    const timeTrend = dayData?.rows.map((row) => ({ label: row.label, value: Math.round(row.avg_time_ms / 60000) })) ?? [];
    const masteryTrend = dayData?.rows.map((row) => ({ label: row.label, value: row.mastery_pct ?? 0 })) ?? [];

    const sessionSummary = useMemo(() => {
        const values = dayData?.rows.map((row) => row.sessions) ?? [];
        if (!values.length) return [];
        return [
            formatSummaryStat("mean", mean(values)),
            formatSummaryStat("p95", percentile(values, 95)),
        ];
    }, [dayData]);

    const completionSummary = useMemo(() => {
        const values = dayData?.rows.map((row) => row.avg_completion_pct) ?? [];
        if (!values.length) return [];
        return [
            formatSummaryStat("mean", mean(values)),
            formatSummaryStat("p95", percentile(values, 95)),
        ];
    }, [dayData]);

    const timeSummary = useMemo(() => {
        const values = dayData?.rows.map((row) => row.avg_time_ms / 60000) ?? [];
        if (!values.length) return [];
        return [
            formatSummaryStat("mean", mean(values)),
            formatSummaryStat("p95", percentile(values, 95)),
        ];
    }, [dayData]);

    const accuracySummary = useMemo(() => {
        const values = dayData?.rows.map((row) => row.accuracy_pct) ?? [];
        if (!values.length) return [];
        return [
            formatSummaryStat("mean", mean(values)),
            formatSummaryStat("p95", percentile(values, 95)),
        ];
    }, [dayData]);

    const masterySummary = useMemo(() => {
        const values = dayData?.rows.map((row) => row.mastery_pct ?? 0) ?? [];
        if (!values.length) return [];
        return [
            formatSummaryStat("mean", mean(values)),
            formatSummaryStat("p95", percentile(values, 95)),
        ];
    }, [dayData]);

    const hasTimeBreakdownData = useMemo(() => {
        const rows = lessonData?.rows ?? [];
        return rows.some((row) => {
            const tb = row.time_breakdown;
            if (!tb) return false;
            return (tb.ai_speaking_ms || 0) + (tb.user_speaking_ms || 0) + (tb.tech_issues_ms || 0) > 0;
        });
    }, [lessonData]);

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <p>Loading session metrics...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <p>{error}</p>
                <Button variant="outline" className="mt-4" onClick={() => loadData(filters)}>Retry</Button>
            </div>
        );
    }

    if (!lessonData || !dayData) return null;

    const summary = lessonData.summary;

    return (
        <div className="space-y-6">
            <SectionHeader
                title="Sessions"
                description="Lesson-efficiency view showing which lessons are taking too long and where student effort is going."
            />

            <AnalyticsFilters filters={filters} onApply={handleFilterApply} filterDisplay={filterDisplay} onFilterDisplayChange={onFilterDisplayChange} />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
                <MetricCard
                    title="Sessions"
                    value={formatCount(summary.total_sessions)}
                    hint="Total sessions represented in the lesson-efficiency view."
                    tone="info"
                    trend={sessionTrend}
                    summaries={sessionSummary}
                />
                <MetricCard
                    title="Avg Completion"
                    value={formatPercent(summary.avg_completion_pct)}
                    hint="Average completion across the lesson-scoped session set."
                    tone={summary.avg_completion_pct >= 75 ? "success" : summary.avg_completion_pct >= 40 ? "warning" : "danger"}
                    trend={completionTrend}
                    summaries={completionSummary}
                />
                <MetricCard
                    title="Avg Time"
                    value={formatMinutesFromMs(summary.avg_time_ms)}
                    hint="Average session duration."
                    tone="warning"
                    trend={timeTrend}
                    summaries={timeSummary}
                />
                <MetricCard
                    title="Avg Accuracy"
                    value={formatPercent(summary.avg_accuracy_pct)}
                    hint="Correctness across available response data."
                    tone={summary.avg_accuracy_pct >= 75 ? "success" : summary.avg_accuracy_pct >= 40 ? "warning" : "danger"}
                    summaries={accuracySummary}
                />
                <MetricCard
                    title="Mastery %"
                    value={formatPercent(summary.mastery_pct ?? 0)}
                    hint="% of session users who scored ≥ 75 on exit ticket."
                    tone={(summary.mastery_pct ?? 0) >= 70 ? "success" : (summary.mastery_pct ?? 0) >= 40 ? "warning" : "danger"}
                    trend={masteryTrend}
                    summaries={masterySummary}
                />
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.65fr_1fr]">
                <ChartCard
                    title="Session Volume & Completion Trend"
                    description="High-level pulse of the sessions experience. Bars = session count; lines = completion % and mastery %."
                >
                    {dayData.rows.length > 0 ? (
                        <ResponsiveContainer width="100%" height={320}>
                            <ComposedChart data={dayData.rows}>
                                <CartesianGrid strokeDasharray="3 3" stroke={ANALYTICS_COLORS.grid} />
                                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Legend />
                                <Bar yAxisId="left" dataKey="sessions" fill={ANALYTICS_SERIES.sessions} name="Sessions" />
                                <Line yAxisId="right" type="monotone" dataKey="avg_completion_pct" stroke={ANALYTICS_SERIES.completion} strokeWidth={2} dot={false} name="Completion %" />
                                <Line yAxisId="right" type="monotone" dataKey="mastery_pct" stroke="#f97316" strokeWidth={2} dot={false} name="Mastery %" />
                            </ComposedChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="py-12 text-center text-muted-foreground">No daily session data</p>
                    )}
                </ChartCard>

                <ChartCard
                    title="Started / Completed / Mastered"
                    description="Stacked daily view: total started (top), completed subset (green), mastered subset (teal)."
                >
                    {dayData.rows.length > 0 ? (
                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart data={dayData.rows.map((row) => ({
                                label: row.label,
                                mastered: row.mastered_count ?? 0,
                                completed_not_mastered: Math.max(0, (row.completed_count ?? 0) - (row.mastered_count ?? 0)),
                                started_not_completed: Math.max(0, row.sessions - (row.completed_count ?? 0)),
                            }))}>
                                <CartesianGrid strokeDasharray="3 3" stroke={ANALYTICS_COLORS.grid} />
                                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="mastered" stackId="a" fill={ANALYTICS_COLORS.mastery} name="Mastered" />
                                <Bar dataKey="completed_not_mastered" stackId="a" fill={ANALYTICS_SERIES.completion} name="Completed" />
                                <Bar dataKey="started_not_completed" stackId="a" fill={ANALYTICS_COLORS.neutral} name="Started only" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="py-12 text-center text-muted-foreground">No daily session data</p>
                    )}
                </ChartCard>
            </div>

            {/* Row 1: 3 lesson-level charts side by side */}
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                <ChartCard
                    title="Slowest Lessons"
                    description="Ranked by average time so the longest content shows up first."
                >
                    {sortedLessons.length > 0 ? (
                        <ResponsiveContainer width="100%" height={340}>
                            <BarChart data={sortedLessons.map((r) => ({ ...r, avg_time_min: Math.round(r.avg_time_ms / 60000) }))} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke={ANALYTICS_COLORS.grid} />
                                <XAxis type="number" tick={{ fontSize: 11 }} unit="m" />
                                <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={160} />
                                <Tooltip formatter={(value?: number) => (value == null ? "-" : `${value}m`)} />
                                <Bar dataKey="avg_time_min" fill={ANALYTICS_SERIES.time} radius={[0, 6, 6, 0]} name="Avg Time" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="py-12 text-center text-muted-foreground">No lesson-level data</p>
                    )}
                </ChartCard>

                <ChartCard
                    title="Scaffolding Mix"
                    description="The mix matters more than raw volume here, so this stays a lesson-level diagnostic."
                >
                    {sortedLessons.length > 0 ? (
                        <ResponsiveContainer width="100%" height={340}>
                            <BarChart data={sortedLessons} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke={ANALYTICS_COLORS.grid} />
                                <XAxis type="number" tick={{ fontSize: 11 }} />
                                <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={160} />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="scaffolding.none" stackId="scaffold" fill={ANALYTICS_COLORS.completion} name="None" />
                                <Bar dataKey="scaffolding.low" stackId="scaffold" fill={ANALYTICS_COLORS.scaffoldingLow} name="Low" />
                                <Bar dataKey="scaffolding.medium" stackId="scaffold" fill={ANALYTICS_COLORS.scaffoldingMed} name="Medium" />
                                <Bar dataKey="scaffolding.high" stackId="scaffold" fill={ANALYTICS_COLORS.scaffoldingHigh} name="High" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="py-12 text-center text-muted-foreground">No lesson-level data</p>
                    )}
                </ChartCard>

                <ChartCard
                    title="Correct / Incorrect / Help"
                    description="Separates hard content from support-heavy content at the lesson level."
                >
                    {sortedLessons.length > 0 ? (
                        <ResponsiveContainer width="100%" height={340}>
                            <BarChart data={sortedLessons} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke={ANALYTICS_COLORS.grid} />
                                <XAxis type="number" tick={{ fontSize: 11 }} />
                                <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={160} />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="correct" stackId="a" fill={ANALYTICS_COLORS.completion} name="Correct" />
                                <Bar dataKey="incorrect" stackId="a" fill={ANALYTICS_COLORS.risk} name="Incorrect" />
                                <Bar dataKey="help_requests" stackId="a" fill={ANALYTICS_COLORS.scaffoldingMed} name="Help" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="py-12 text-center text-muted-foreground">No lesson-level data</p>
                    )}
                </ChartCard>
            </div>

            {/* Row 2: Lesson Risk Table full-width */}
            <ChartCard
                title="Lesson Risk Table"
                description="Full lesson breakdown — click column headers to sort."
                contentClassName="p-0"
            >
                {sortedLessonsTable.length === 0 ? (
                    <p className="py-12 text-center text-muted-foreground">No lesson-level data</p>
                ) : (
                    <div className="overflow-x-auto">
                        <div className="max-h-[560px] overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 z-10 bg-card">
                                <tr className="border-b border-border bg-muted/50">
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Lesson</th>
                                    <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleLessonSort("sessions")}>
                                        Sessions<SortIcon field={"sessions" as never} currentField={lessonSort.key as never} sortOrder={lessonSort.dir} />
                                    </th>
                                    <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleLessonSort("avg_time_ms")}>
                                        Avg Time<SortIcon field={"avg_time_ms" as never} currentField={lessonSort.key as never} sortOrder={lessonSort.dir} />
                                    </th>
                                    <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleLessonSort("avg_completion_pct")}>
                                        Completion<SortIcon field={"avg_completion_pct" as never} currentField={lessonSort.key as never} sortOrder={lessonSort.dir} />
                                    </th>
                                    <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleLessonSort("completed_count")}>
                                        Completed<SortIcon field={"completed_count" as never} currentField={lessonSort.key as never} sortOrder={lessonSort.dir} />
                                    </th>
                                    <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleLessonSort("mastery_pct")}>
                                        Mastery<SortIcon field={"mastery_pct" as never} currentField={lessonSort.key as never} sortOrder={lessonSort.dir} />
                                    </th>
                                    <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleLessonSort("mastered_count")}>
                                        Mastered<SortIcon field={"mastered_count" as never} currentField={lessonSort.key as never} sortOrder={lessonSort.dir} />
                                    </th>
                                    <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleLessonSort("accuracy_pct")}>
                                        Accuracy<SortIcon field={"accuracy_pct" as never} currentField={lessonSort.key as never} sortOrder={lessonSort.dir} />
                                    </th>
                                    <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleLessonSort("correct")}>
                                        Correct<SortIcon field={"correct" as never} currentField={lessonSort.key as never} sortOrder={lessonSort.dir} />
                                    </th>
                                    <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleLessonSort("incorrect")}>
                                        Incorrect<SortIcon field={"incorrect" as never} currentField={lessonSort.key as never} sortOrder={lessonSort.dir} />
                                    </th>
                                    <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleLessonSort("help_requests")}>
                                        Help<SortIcon field={"help_requests" as never} currentField={lessonSort.key as never} sortOrder={lessonSort.dir} />
                                    </th>
                                    <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleLessonSort("messages")}>
                                        Messages<SortIcon field={"messages" as never} currentField={lessonSort.key as never} sortOrder={lessonSort.dir} />
                                    </th>
                                    <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleLessonSort("avg_stt_ms")}>
                                        Avg STT<SortIcon field={"avg_stt_ms" as never} currentField={lessonSort.key as never} sortOrder={lessonSort.dir} />
                                    </th>
                                    <th className="cursor-pointer select-none px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground" onClick={() => handleLessonSort("avg_llm_ms")}>
                                        Avg LLM<SortIcon field={"avg_llm_ms" as never} currentField={lessonSort.key as never} sortOrder={lessonSort.dir} />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedLessonsTable.map((lesson) => (
                                    <tr key={lesson.key} className="border-b border-border/50">
                                        <td className="px-4 py-3 font-medium">{lesson.label}</td>
                                        <td className="px-4 py-3 text-right">{formatCount(lesson.sessions)}</td>
                                        <td className="px-4 py-3 text-right">{formatMinutesFromMs(lesson.avg_time_ms)}</td>
                                        <td className="px-4 py-3 text-right">{formatPercent(lesson.avg_completion_pct)}</td>
                                        <td className="px-4 py-3 text-right">{formatCount(lesson.completed_count)}</td>
                                        <td className="px-4 py-3 text-right">{lesson.mastery_pct != null ? formatPercent(lesson.mastery_pct) : "-"}</td>
                                        <td className="px-4 py-3 text-right">{formatCount(lesson.mastered_count)}</td>
                                        <td className="px-4 py-3 text-right">{formatPercent(lesson.accuracy_pct)}</td>
                                        <td className="px-4 py-3 text-right">{formatCount(lesson.correct)}</td>
                                        <td className="px-4 py-3 text-right">{formatCount(lesson.incorrect)}</td>
                                        <td className="px-4 py-3 text-right">{formatCount(lesson.help_requests)}</td>
                                        <td className="px-4 py-3 text-right">{formatCount(lesson.messages)}</td>
                                        <td className="px-4 py-3 text-right">{formatMinutesFromMs(lesson.avg_stt_ms)}</td>
                                        <td className="px-4 py-3 text-right">{formatMinutesFromMs(lesson.avg_llm_ms)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </div>
                )}
            </ChartCard>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <ChartCard
                    title="Time Breakdown"
                    description="Useful but secondary; this stays below the main lesson-level story."
                >
                    {lessonData && lessonData.rows.length > 0 && hasTimeBreakdownData ? (
                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart data={lessonData.rows.slice(0, 10)} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke={ANALYTICS_COLORS.grid} />
                                <XAxis type="number" tick={{ fontSize: 11 }} />
                                <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={130} />
                                <Tooltip formatter={(value?: number) => (value == null ? "-" : formatMinutesFromMs(value))} />
                                <Legend />
                                <Bar dataKey="time_breakdown.ai_speaking_ms" stackId="time" fill={ANALYTICS_SERIES.sessions} name="AI speaking" />
                                <Bar dataKey="time_breakdown.user_speaking_ms" stackId="time" fill={ANALYTICS_SERIES.completion} name="Student speaking" />
                                <Bar dataKey="time_breakdown.tech_issues_ms" stackId="time" fill={ANALYTICS_COLORS.risk} name="Tech issues" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="py-12 text-center text-muted-foreground">No time breakdown data yet</p>
                    )}
                </ChartCard>

                <PlaceholderCard
                    title="Duration Distribution & Selected-Lesson Diagnostics"
                    description="The final Sessions board should include richer distribution and selected-lesson drilldown views, but those need stronger backend support than this migration pass should take on."
                />
            </div>
        </div>
    );
};
